import * as React from 'react';
import SubscriptionStatusAlert from '../common/SubscriptionStatusAlert';
import { BillingDetailsSummary } from './details/BillingDetailsSummary';
import { BillingDetailsInjectorProperties, injectBillingDetails } from '../hocs/BillingDetailsInjector';
import { BillingPeriodInjectorProperties, injectBillingPeriod } from '../hocs/BillingPeriodInjector';
import { BillingInvoicesInjectorProperties, injectBillingInvoices } from '../hocs/BillingInvoicesInjector';
import { formatAmount, getDateFormatWithoutDay } from '../../utils/utilFunctions';
import { DomainInjectorProperties, injectDomain } from '../hocs/DomainInjector';
import { PageRoutes } from '../root/Routes';
import { Invoice } from 'fs-api-client';
import { PaySingleInvoiceModal } from '../PaySingleInvoiceModal';
import { CreditCardIcon } from '../common/Icons';
import { Message } from '../common/Message';
import { Link } from 'react-router-dom';
import { suspendedErrorMessage, suspendedErrorTitle } from '../../utils/constants';
import { FilespaceBreakdownModal } from '../FilespaceBreakdownModal';
import { fetchAndUpdateAccountSetting, injectProfile } from '../hocs/ProfileInjector';
import FilespaceBreakdownForecast from '../FilespaceBreakdownForecast';

interface BillingProperties extends BillingDetailsInjectorProperties,
    DomainInjectorProperties,
    BillingPeriodInjectorProperties,
    BillingInvoicesInjectorProperties {
    history: any;
    isUserSuspended: boolean;
}

interface BillingState {
    selectedInvoice: Invoice | null;
    isInvoiceModalShown: boolean;
}

class Billing extends React.Component<BillingProperties, BillingState> {
    constructor(props: BillingProperties) {
        super(props);

        this.refetchBillingDetails = this.refetchBillingDetails.bind(this);
        this.onInvoiceClicked = this.onInvoiceClicked.bind(this);
        this.onAddCreditCard = this.onAddCreditCard.bind(this);
        this.onEditCreditCard = this.onEditCreditCard.bind(this);
        this.renderInvoiceInfoModal = this.renderInvoiceInfoModal.bind(this);
        this.showModal = this.showModal.bind(this);

        this.state = {
            selectedInvoice: null,
            isInvoiceModalShown: false,
        };
    }

    public onAddCreditCard(): void {
        this.props.history.push(PageRoutes.BillingDetails);
    }

    public onEditCreditCard(): void {
        this.props.history.push(PageRoutes.BillingDetails);
    }

    renderInvoiceInfoModal(): JSX.Element | null {
        if (!this.state.selectedInvoice) {
            return null;
        }
        return (
            <div>
                <PaySingleInvoiceModal
                    onPaymentSuccessful={this.refetchBillingDetails}
                    paymentDetails={this.props.paymentDetails}
                    invoice={this.state.selectedInvoice}
                />
            </div>
        );
    }

    public render(): JSX.Element {
        return (
            <React.Fragment>
                <header className="billing__header">
                    <div className="fix__container">
                        <h1 className="title">
                            Billing dashboard
                        </h1>
                    </div>
                    <div className="fix__container">
                        {this.props.isUserSuspended
                        && (
                            <Message
                                message={suspendedErrorMessage}
                                title={suspendedErrorTitle}
                            />
                        )}
                        {!this.props.isUserSuspended && this.renderStats()}
                    </div>
                    {!this.props.isUserSuspended && (<FilespaceBreakdownForecast
                        filespaceBreakdown={this.props.billingPeriod.filespaceBreakdown}
                    />)}
                </header>
                <div className="fix__container">
                    <SubscriptionStatusAlert isPaymentReferenceTextDisabled={true}/>
                    <div className="billing">
                        {this.renderInvoices()}
                    </div>
                    {this.state.isInvoiceModalShown && this.props.paymentDetails.card && this.renderInvoiceInfoModal()}
                </div>
            </React.Fragment>
        );
    }

    componentDidMount() {
        this.props.fetchBillingPeriod(false);
    }

    private getCurrentPeriodLabel() {
        return `Current period${this.props.billingPeriod.trialDays !== undefined ? ' (trial)' : ''}`;
    }

    private async refetchBillingDetails() {
        await this.props.fetchBillingInvoices(false);
        await fetchAndUpdateAccountSetting();
    }

    private renderStats(): JSX.Element {
        return (
            <div className="stats__resume">
                <ul>
                    <li>
                        <strong>{getDateFormatWithoutDay(this.props.billingPeriod.start)} - {getDateFormatWithoutDay(this.props.billingPeriod.end)}</strong>
                        <span>{this.getCurrentPeriodLabel()}</span>
                    </li>
                    <li>
                        <strong>{formatAmount(this.props.billingPeriod.amountDue, this.props.billingPeriod.currency)}</strong>
                        <span>Period-to-date balance</span>
                    </li>
                </ul>
                <ul>
                    <li>
                        {formatAmount(this.props.billingPeriod.amountDueForecast, this.props.billingPeriod.currency)}
                        <br/>
                        <span className="ttip">
                            <em>Period-end forecast</em>
                            <div>Your forecasted costs for the current period-end. Updated daily.</div>
                        </span>
                    </li>
                </ul>
            </div>
        );
    }

    private renderInvoices(): JSX.Element {
        if (this.props.billingInvoices.length < 1) {
            return this.renderInvoicesPlaceholder();
        } else {
            return (
                <React.Fragment>
                    <div className="flex__row">
                        <div className="stretch__column">
                            <FilespaceBreakdownModal
                                invoice={this.state.selectedInvoice}
                            />
                            <div className="invoices table-container">
                                <span className="box__label">Past Invoices</span>
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th scope="col" className="left">Date</th>
                                        <th scope="col" className="hidden left">Item</th>
                                        <th scope="col">Price</th>
                                        <th scope="col">&nbsp;</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {this.props.billingInvoices.map((invoice) => {
                                        let isInvoiceUnpaid = ((invoice.paymentStatus === Invoice.PaymentStatusEnum.NotPaid ||
                                            invoice.paymentStatus === Invoice.PaymentStatusEnum.PastDue));
                                        const {status, statusClassName} =
                                            resolveInvoicePaymentStatusData(invoice);
                                        return (
                                            <tr key={invoice.pdfUrl}>
                                                <td className="left">{getDateFormatWithoutDay(invoice.date)}</td>
                                                <td className="hidden left">
                                                    {
                                                        invoice.lineDescriptions.map((description) => {
                                                            return (
                                                                <React.Fragment key={description}>
                                                                    {description}
                                                                    <br/>
                                                                </React.Fragment>
                                                            );
                                                        })
                                                    }
                                                    <div className="flex-left">
                                                        {invoice.filespaceBreakdown && invoice.filespaceBreakdown.length > 0 && (
                                                            <a
                                                                href="#"
                                                                data-toggle="modal"
                                                                className="detailLink"
                                                                data-target="#filespaceBreakdownModal"
                                                                onClick={() => this.onInvoiceClicked(invoice)}
                                                            >Details
                                                            </a>
                                                        )}
                                                        <a
                                                            href={invoice.pdfUrl}
                                                            className="detailLink"
                                                            target="_blank"
                                                        >
                                                            PDF
                                                        </a>
                                                    </div>
                                                </td>
                                                <td>
                                                    {isInvoiceUnpaid && (
                                                        <strong className="green">
                                                            {formatAmount(invoice.amountDue, invoice.currency)}
                                                        </strong>
                                                    )}
                                                    {!isInvoiceUnpaid && (
                                                        formatAmount(invoice.amountDue, invoice.currency)
                                                    )}
                                                </td>
                                                <td>
                                                    {isInvoiceUnpaid &&
                                                        this.props.paymentDetails.card && (
                                                        <button
                                                            className="pay-button"
                                                            data-toggle="modal"
                                                            data-target="#paySingleInvoiceModal"
                                                            onClick={() => {
                                                                this.onInvoiceClicked(invoice);
                                                                this.showModal();
                                                            }}

                                                        >
                                                            Pay now
                                                            <CreditCardIcon className="icon" width="19px"/>
                                                        </button>)}

                                                    {isInvoiceUnpaid &&
                                                        !this.props.paymentDetails.card && (
                                                        <Link to={'/billingdetails'}>
                                                            <button className="pay-button">Pay now
                                                                <CreditCardIcon className="icon" width="19px"/>
                                                            </button>
                                                        </Link>)}

                                                    {invoice.paymentStatus === Invoice.PaymentStatusEnum.Paid
                                                    && (
                                                        <span className={statusClassName}>{status}</span>
                                                    )}

                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {this.renderBillingDetailsSummary()}
                    </div>
                </React.Fragment>
            );
        }
    }

    private onInvoiceClicked(invoice: Invoice) {
        this.setState({selectedInvoice: invoice});
    }

    private showModal() {
        this.setState({isInvoiceModalShown: true});
    }

    private renderInvoicesPlaceholder(): JSX.Element {
        return (
            <div className="flex__row">
                <div className="stretch__column">
                    <div className="invoices table-container disable">
                        <span className="box__label">Past Invoices</span>
                        <h3>You don't have any invoices yet.</h3>
                        <p className="bottom">
                            Your first invoice will be ready on {getDateFormatWithoutDay(this.props.billingPeriod.end)}.
                        </p>
                    </div>
                </div>
                {this.renderBillingDetailsSummary()}
            </div>
        );
    }

    private renderBillingDetailsSummary(): JSX.Element {
        const billingDetails = this.props.paymentDetails;
        return (
            <BillingDetailsSummary
                billingName={billingDetails.name}
                billingAddress={billingDetails.billingAddress}
                companyName={billingDetails.description}
                card={billingDetails.card}
                onAdd={this.onAddCreditCard}
                onEdit={this.onEditCreditCard}
            />
        );
    }
}

function resolveInvoicePaymentStatusData(invoice: Invoice): {
    status: string,
    statusClassName: string
} {
    let status = 'Unknown';
    let className = 'table__label orange left';
    switch (invoice.paymentStatus) {
        case Invoice.PaymentStatusEnum.NotPaid:
            status = 'Not paid';
            break;
        case Invoice.PaymentStatusEnum.PastDue:
            status = 'Past due';
            break;
        case Invoice.PaymentStatusEnum.Paid:
            status = 'Paid';
            className = 'table__label green left';
            break;
        case Invoice.PaymentStatusEnum.Voided:
            status = 'Voided';
            className = 'table__label green left';
            break;
        default:
    }
    return {status: status, statusClassName: className};
}

function injectBillingDetailsIntoBilling(): React.ComponentType<BillingPeriodInjectorProperties> {
    return (injectBillingDetails(Billing) as any) as React.ComponentType<BillingPeriodInjectorProperties>;
}

function injectBillingPeriodIntoBilling(): React.ComponentType<BillingInvoicesInjectorProperties> {
    return (injectBillingPeriod(injectBillingDetailsIntoBilling()) as any) as React.ComponentType<BillingInvoicesInjectorProperties>;
}

function injectBillingInvoicesIntoBilling(): React.ComponentType<DomainInjectorProperties> {
    return (injectBillingInvoices(injectBillingPeriodIntoBilling()) as any) as React.ComponentType<DomainInjectorProperties>;
}

function injectDomainIntoBilling() {
    return injectDomain(injectBillingInvoicesIntoBilling());
}

function injectProfileIntoBilling() {
    return injectProfile(injectDomainIntoBilling() as any);
}

export default injectProfileIntoBilling();



// WEBPACK FOOTER //
// ./src/webportal/components/billing/Billing.tsx